import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Heading,
  HStack,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  SimpleGrid,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
  VStack
} from '@chakra-ui/react'
import React, { useRef } from 'react'
import {
  FiAlertTriangle,
  FiCheck,
  FiDownload,
  FiFile,
  FiMapPin,
  FiRefreshCw,
  FiUpload,
  FiUserCheck,
  FiUserX,
  FiX
} from 'react-icons/fi'
import { useCSVImport } from '../hooks/useCSVImport'

const CSVImport: React.FC = () => {
  const {
    state,
    file,
    preview,
    result,
    error,
    progress,
    loadPreview,
    importCSV,
    reset,
    generateErrorReport,
  } = useCSVImport();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  const handleFileSelect = (selectedFile: File) => {
    loadPreview(selectedFile);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileSelect(droppedFile);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleConfirmImport = async () => {
    onClose();
    await importCSV();
  };

  const downloadErrorReport = () => {
    const report = generateErrorReport();
    const blob = new Blob([report], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `relatorio-importacao-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const renderFileUpload = () => (
    <Card>
      <CardHeader>
        <Heading size="md" display="flex" alignItems="center">
          <Icon as={FiUpload} mr={2} color="blue.500" />
          Importar Inscrições do Consulado
        </Heading>
      </CardHeader>
      <CardBody>
        <VStack spacing={6}>
          <Box
            w="100%"
            h="200px"
            border="2px dashed"
            borderColor={borderColor}
            borderRadius="lg"
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            cursor="pointer"
            transition="all 0.2s"
            _hover={{ borderColor: 'blue.400', bg: hoverBg }}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={() => fileInputRef.current?.click()}
          >
            <Icon as={FiFile} boxSize={12} color="gray.400" mb={4} />
            <Text fontSize="lg" fontWeight="medium" color="gray.600" mb={2}>
              Arraste o arquivo CSV aqui
            </Text>
            <Text fontSize="sm" color="gray.500">
              ou clique para selecionar
            </Text>
            <Text fontSize="xs" color="gray.400" mt={2}>
              Máximo 10MB • Apenas arquivos .csv
            </Text>
          </Box>

          <Input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            display="none"
            onChange={(e) => {
              const selectedFile = e.target.files?.[0];
              if (selectedFile) handleFileSelect(selectedFile);
            }}
          />

          <Button
            colorScheme="blue"
            size="lg"
            leftIcon={<FiUpload />}
            onClick={() => fileInputRef.current?.click()}
          >
            Selecionar Arquivo CSV
          </Button>
        </VStack>
      </CardBody>
    </Card>
  );

  const renderPreview = () => (
    <Card>
      <CardHeader>
        <HStack justify="space-between">
          <Heading size="md" display="flex" alignItems="center">
            <Icon as={FiFile} mr={2} color="green.500" />
            Preview do Arquivo
          </Heading>
          <Badge colorScheme="blue" fontSize="sm">
            {preview?.totalRows} registros
          </Badge>
        </HStack>
      </CardHeader>
      <CardBody>
        <VStack spacing={4}>
          <HStack w="100%" justify="space-between">
            <Text fontSize="sm" color="gray.600">
              <strong>Arquivo:</strong> {file?.name}
            </Text>
            <Text fontSize="sm" color="gray.600">
              <strong>Tamanho:</strong> {((file?.size || 0) / 1024).toFixed(1)} KB
            </Text>
          </HStack>

          {preview && (
            <Box w="100%" overflowX="auto">
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    {preview.headers.map((header, index) => (
                      <Th key={index}>{header}</Th>
                    ))}
                  </Tr>
                </Thead>
                <Tbody>
                  {preview.rows.map((row, rowIndex) => (
                    <Tr key={rowIndex}>
                      {row.map((cell, cellIndex) => (
                        <Td key={cellIndex}>{cell}</Td>
                      ))}
                    </Tr>
                  ))}
                </Tbody>
              </Table>
              {preview.totalRows > 5 && (
                <Text fontSize="xs" color="gray.500" mt={2} textAlign="center">
                  Mostrando apenas os primeiros 5 registros de {preview.totalRows}
                </Text>
              )}
            </Box>
          )}

          <HStack spacing={4} w="100%">
            <Button
              colorScheme="green"
              size="lg"
              leftIcon={<FiCheck />}
              onClick={onOpen}
              flex={1}
            >
              Confirmar Importação
            </Button>
            <Button
              variant="outline"
              size="lg"
              leftIcon={<FiX />}
              onClick={reset}
            >
              Cancelar
            </Button>
          </HStack>
        </VStack>
      </CardBody>
    </Card>
  );

  const renderUploading = () => (
    <Card>
      <CardBody>
        <VStack spacing={6}>
          <Icon as={FiRefreshCw} boxSize={16} color="blue.500" className="spin" />
          <VStack spacing={2}>
            <Text fontSize="lg" fontWeight="medium">
              Importando inscrições...
            </Text>
            <Text fontSize="sm" color="gray.600">
              Aguarde enquanto processamos o arquivo
            </Text>
          </VStack>
          <Box w="100%">
            <Progress value={progress} colorScheme="blue" size="lg" hasStripe isAnimated />
            <Text fontSize="sm" color="gray.600" mt={2} textAlign="center">
              {progress.toFixed(1)}%
            </Text>
          </Box>
        </VStack>
      </CardBody>
    </Card>
  );

  const renderSuccess = () => (
    <Card>
      <CardHeader>
        <Heading size="md" display="flex" alignItems="center" color="green.500">
          <Icon as={FiCheck} mr={2} />
          Importação Concluída
        </Heading>
      </CardHeader>
      <CardBody>
        <VStack spacing={6}>
          <Alert status="success" borderRadius="lg">
            <AlertIcon />
            <Box>
              <AlertTitle>Sucesso!</AlertTitle>
              <AlertDescription>{result?.message}</AlertDescription>
            </Box>
          </Alert>

          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} w="100%">
            <Stat bg="green.50" p={4} borderRadius="lg" border="1px solid" borderColor="green.200">
              <StatLabel display="flex" alignItems="center">
                <Icon as={FiUserCheck} mr={2} color="green.500" />
                Criados
              </StatLabel>
              <StatNumber color="green.600">{result?.totalImportados ?? 0}</StatNumber>
              <StatHelpText>Novos usuários</StatHelpText>
            </Stat>

            <Stat bg="blue.50" p={4} borderRadius="lg" border="1px solid" borderColor="blue.200">
              <StatLabel display="flex" alignItems="center">
                <Icon as={FiRefreshCw} mr={2} color="blue.500" />
                Atualizados
              </StatLabel>
              <StatNumber color="blue.600">{result?.totalAtualizados ?? 0}</StatNumber>
              <StatHelpText>Usuários existentes</StatHelpText>
            </Stat>

            <Stat bg="yellow.50" p={4} borderRadius="lg" border="1px solid" borderColor="yellow.200">
              <StatLabel display="flex" alignItems="center">
                <Icon as={FiAlertTriangle} mr={2} color="yellow.500" />
                Rejeitados
              </StatLabel>
              <StatNumber color="yellow.600">{result?.rejeitadosPorStatus?.total ?? 0}</StatNumber>
              <StatHelpText>Por status inválido</StatHelpText>
            </Stat>

            <Stat bg="red.50" p={4} borderRadius="lg" border="1px solid" borderColor="red.200">
              <StatLabel display="flex" alignItems="center">
                <Icon as={FiUserX} mr={2} color="red.500" />
                Erros
              </StatLabel>
              <StatNumber color="red.600">{result?.erros?.length ?? 0}</StatNumber>
              <StatHelpText>Falhas no processo</StatHelpText>
            </Stat>
          </SimpleGrid>

          <Box w="100%">
            <HStack justify="space-between" mb={4}>
              <Text fontSize="lg" fontWeight="medium" display="flex" alignItems="center">
                <Icon as={FiMapPin} mr={2} color="blue.500" />
                Consulado: {result?.store}
              </Text>
            </HStack>

            <Accordion allowToggle>
              {result?.rejeitadosPorStatus && result.rejeitadosPorStatus.total > 0 && (
                <AccordionItem>
                  <AccordionButton>
                    <Box flex="1" textAlign="left">
                      <Badge colorScheme="yellow" mr={2}>
                        {result.rejeitadosPorStatus.total}
                      </Badge>
                      Usuários Rejeitados por Status
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={4}>
                    <VStack align="start" spacing={2}>
                      {result.rejeitadosPorStatus.exemplos?.map((item, index) => (
                        <Text key={index} fontSize="sm">
                          <strong>{item.usuario}:</strong> {item.status}
                        </Text>
                      ))}
                    </VStack>
                  </AccordionPanel>
                </AccordionItem>
              )}

              {result?.atualizados && result.atualizados.total > 0 && (
                <AccordionItem>
                  <AccordionButton>
                    <Box flex="1" textAlign="left">
                      <Badge colorScheme="blue" mr={2}>
                        {result.atualizados.total}
                      </Badge>
                      Usuários Atualizados
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={4}>
                    <VStack align="start" spacing={2}>
                      {result.atualizados.exemplos?.map((item, index) => (
                        <Text key={index} fontSize="sm">
                          <strong>{item.usuario}:</strong> {item.detalhes}
                        </Text>
                      ))}
                    </VStack>
                  </AccordionPanel>
                </AccordionItem>
              )}

              {result?.erros && result.erros.length > 0 && (
                <AccordionItem>
                  <AccordionButton>
                    <Box flex="1" textAlign="left">
                      <Badge colorScheme="red" mr={2}>
                        {result.erros.length}
                      </Badge>
                      Erros Encontrados
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={4}>
                    <VStack align="start" spacing={2}>
                      {result.erros.map((erro, index) => (
                        <Text key={index} fontSize="sm">
                          <strong>{erro.usuario}:</strong> {erro.regra}
                        </Text>
                      ))}
                    </VStack>
                  </AccordionPanel>
                </AccordionItem>
              )}
            </Accordion>
          </Box>

          <HStack spacing={4} w="100%">
            <Button
              colorScheme="blue"
              leftIcon={<FiDownload />}
              onClick={downloadErrorReport}
              flex={1}
            >
              Baixar Relatório
            </Button>
            <Button
              variant="outline"
              leftIcon={<FiRefreshCw />}
              onClick={reset}
            >
              Nova Importação
            </Button>
          </HStack>
        </VStack>
      </CardBody>
    </Card>
  );

  const renderError = () => (
    <Card>
      <CardBody>
        <VStack spacing={6}>
          <Alert status="error" borderRadius="lg">
            <AlertIcon />
            <Box>
              <AlertTitle>Erro na Importação</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Box>
          </Alert>

          <Button
            colorScheme="blue"
            leftIcon={<FiRefreshCw />}
            onClick={reset}
            size="lg"
          >
            Tentar Novamente
          </Button>
        </VStack>
      </CardBody>
    </Card>
  );

  return (
    <Box maxW="6xl" mx="auto" p={6}>
      {state === 'idle' && renderFileUpload()}
      {state === 'preview' && renderPreview()}
      {state === 'uploading' && renderUploading()}
      {state === 'success' && renderSuccess()}
      {state === 'error' && renderError()}

      {/* Modal de Confirmação */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader display="flex" alignItems="center">
            <Icon as={FiAlertTriangle} mr={2} color="orange.500" />
            Confirmar Importação
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="start">
              <Text>
                Você está prestes a importar <strong>{preview?.totalRows}</strong> registros
                para o consulado <strong>italia-consulado</strong>.
              </Text>
              <Alert status="warning" borderRadius="lg">
                <AlertIcon />
                <Box>
                  <AlertTitle>Atenção!</AlertTitle>
                  <AlertDescription>
                    Esta ação não pode ser desfeita. Certifique-se de que o arquivo está correto.
                  </AlertDescription>
                </Box>
              </Alert>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancelar
            </Button>
            <Button colorScheme="green" onClick={handleConfirmImport}>
              Confirmar Importação
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <style>
        {`
          .spin {
            animation: spin 2s linear infinite;
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </Box>
  );
};

export default CSVImport; 